/**
 * useTypingMetrics — tracks typing behavior for cross-modal analysis.
 *
 * Uses refs (not state) to avoid re-renders on every keystroke.
 * On submit, computes aggregate metrics and resets.
 *
 * Metrics:
 * - avgCharsPerSec: typing speed
 * - maxPauseMs: longest pause between keystrokes
 * - pauseCount: pauses > 2 seconds (hesitation signals)
 * - editCount: backspace/delete presses (self-censoring signals)
 * - totalTimeMs: total typing duration
 */

import { useRef, useCallback } from 'react'

export interface TypingMetrics {
  avgCharsPerSec: number
  maxPauseMs: number
  pauseCount: number
  editCount: number
  totalTimeMs: number
}

const PAUSE_THRESHOLD_MS = 2000

export function useTypingMetrics() {
  const timestamps = useRef<number[]>([])
  const editCount = useRef(0)
  const firstKeystroke = useRef<number | null>(null)

  const recordKeystroke = useCallback((e: React.KeyboardEvent) => {
    const now = Date.now()

    // Track edits (backspace, delete)
    if (e.key === 'Backspace' || e.key === 'Delete') {
      editCount.current++
      return
    }

    // Ignore modifier keys, arrows, etc.
    if (e.key.length > 1 && e.key !== 'Enter') return

    if (firstKeystroke.current === null) {
      firstKeystroke.current = now
    }

    timestamps.current.push(now)
  }, [])

  const getAndReset = useCallback((): TypingMetrics | undefined => {
    const ts = timestamps.current
    if (ts.length < 2) {
      // Not enough data — reset and return undefined
      timestamps.current = []
      editCount.current = 0
      firstKeystroke.current = null
      return undefined
    }

    const totalTimeMs = ts[ts.length - 1] - ts[0]
    const charCount = ts.length

    // Calculate pauses
    let maxPauseMs = 0
    let pauseCount = 0
    for (let i = 1; i < ts.length; i++) {
      const gap = ts[i] - ts[i - 1]
      if (gap > maxPauseMs) maxPauseMs = gap
      if (gap > PAUSE_THRESHOLD_MS) pauseCount++
    }

    const avgCharsPerSec = totalTimeMs > 0
      ? (charCount / totalTimeMs) * 1000
      : 0

    const metrics: TypingMetrics = {
      avgCharsPerSec: Math.round(avgCharsPerSec * 10) / 10,
      maxPauseMs: Math.round(maxPauseMs),
      pauseCount,
      editCount: editCount.current,
      totalTimeMs: Math.round(totalTimeMs),
    }

    // Reset
    timestamps.current = []
    editCount.current = 0
    firstKeystroke.current = null

    return metrics
  }, [])

  return { recordKeystroke, getAndReset }
}
